"use client";

/**
 * 표(table) 블록 전체를 에디터 영역 기준으로 좌/가운데/우 정렬하는 툴바 버튼.
 * 셀 텍스트 정렬(TextAlignButtonWithVA)과 구분되며, 표 블록이 선택된 컨텍스트에서만 노출된다.
 * 정렬값은 table 노드 attr(tableAlignment)에 저장한다(setTableAlignment).
 */

import { useCallback, useMemo } from "react";
import {
  useBlockNoteEditor,
  useComponentsContext,
  useSelectedBlocks,
} from "@blocknote/react";
import type {
  BlockSchema,
  InlineContentSchema,
  StyleSchema,
} from "@blocknote/core";
import type { TableAlignment } from "../extensions/TableAlignmentExtension";
import {
  getTableAlignment,
  setTableAlignment,
} from "../utils/prosemirror-table-utils";

// 표(직사각형) 안에 정렬 위치를 나타내는 작은 막대를 둔 아이콘.
const icons: Record<TableAlignment, JSX.Element> = {
  left: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <rect x="1.5" y="4" width="7" height="8" rx="1" fill="currentColor" stroke="none" />
      <line x1="1.5" y1="1.5" x2="14.5" y2="1.5" />
      <line x1="1.5" y1="14.5" x2="14.5" y2="14.5" />
    </svg>
  ),
  center: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <rect x="4.5" y="4" width="7" height="8" rx="1" fill="currentColor" stroke="none" />
      <line x1="1.5" y1="1.5" x2="14.5" y2="1.5" />
      <line x1="1.5" y1="14.5" x2="14.5" y2="14.5" />
    </svg>
  ),
  right: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <rect x="7.5" y="4" width="7" height="8" rx="1" fill="currentColor" stroke="none" />
      <line x1="1.5" y1="1.5" x2="14.5" y2="1.5" />
      <line x1="1.5" y1="14.5" x2="14.5" y2="14.5" />
    </svg>
  ),
};

const tooltips: Record<TableAlignment, string> = {
  left: "표 왼쪽 정렬",
  center: "표 가운데 정렬",
  right: "표 오른쪽 정렬",
};

export const TableAlignButton = (props: { alignment: TableAlignment }) => {
  const Components = useComponentsContext()!;
  const editor = useBlockNoteEditor<
    BlockSchema,
    InlineContentSchema,
    StyleSchema
  >();

  const selectedBlocks = useSelectedBlocks(editor);

  const tableBlock = useMemo(
    () => selectedBlocks.find((block) => block.type === "table"),
    [selectedBlocks],
  );

  const current = useMemo(() => {
    if (!tableBlock?.id) return "left";
    return getTableAlignment(editor, tableBlock.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, tableBlock, selectedBlocks]);

  const apply = useCallback(() => {
    if (!tableBlock?.id) return;
    editor.focus();
    setTableAlignment(editor, tableBlock.id, props.alignment);
  }, [editor, tableBlock, props.alignment]);

  if (!tableBlock || !editor.isEditable) {
    return null;
  }

  return (
    <Components.FormattingToolbar.Button
      className={"bn-button"}
      data-test={`tableAlign${props.alignment.charAt(0).toUpperCase() + props.alignment.slice(1)}`}
      onClick={apply}
      isSelected={current === props.alignment}
      label={tooltips[props.alignment]}
      mainTooltip={tooltips[props.alignment]}
      icon={icons[props.alignment]}
    />
  );
};
